/**
 * WorkspaceDetailContent - Main content section for workspace detail page
 * Contains search, AI analysis button, and main tabs
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertCircle, Bot, Loader2 } from 'lucide-react';
import WorkspaceMainTabs from '@/modules/app/components/workspace/WorkspaceMainTabs';

interface WorkspaceKeywordData {
  id: string;
  primary_keyword: string;
  data_type: string;
  search_volume?: number;
  created_at: string;
}

interface WorkspaceDetailContentProps {
  allKeywordData: WorkspaceKeywordData[];
  keywordData: WorkspaceKeywordData[];
  searchFilter: string;
  isLoading: boolean;
  workspaceName: string;
  workspaceId: string;
  userId: string;
  isAiAnalysisLoading: boolean;
  aiAnalysisError: Error | null;
  onSearchFilterChange: (value: string) => void;
  onAiAnalysis: () => void;
  getAiErrorMessage: () => string;
  clearAiError: () => void;
}

const WorkspaceDetailContent: React.FC<WorkspaceDetailContentProps> = ({
  allKeywordData,
  keywordData,
  searchFilter,
  isLoading,
  workspaceName,
  workspaceId,
  userId,
  isAiAnalysisLoading,
  aiAnalysisError,
  onSearchFilterChange,
  onAiAnalysis,
  getAiErrorMessage,
  clearAiError,
}) => {
  return (
    <div className="px-6 pb-6 space-y-8">
      {/* Section Header with Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-medium text-foreground">
            Analysis ({allKeywordData.length})
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="search"
            placeholder="Search keywords..."
            value={searchFilter}
            onChange={(e) => onSearchFilterChange(e.target.value)}
            className="w-full sm:w-auto max-w-xs h-9 border-border/50 focus:border-border"
          />          <Button
            onClick={onAiAnalysis}
            disabled={isAiAnalysisLoading || allKeywordData.length === 0}
            size="default"
            className="group relative flex items-center gap-2 bg-black hover:bg-gray-900 text-white shadow-lg hover:shadow-xl transition-all duration-300 font-medium px-6 transform hover:scale-105 active:scale-95"
          >
            {isAiAnalysisLoading ? (
              <div className="relative">
                <Loader2 className="h-4 w-4 animate-spin" />
                <div className="absolute inset-0 rounded-full animate-pulse bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 opacity-30 blur-sm"></div>
              </div>
            ) : (
              <div className="relative">
                <Bot className="h-4 w-4" />
                {/* Beam effect - rotating border around icon only - ALWAYS VISIBLE */}
                <div className="absolute -inset-0.5 rounded-full opacity-100">
                  <div className="absolute inset-0 rounded-full" style={{
                    background: 'conic-gradient(from 0deg, #10b981, #fbbf24, #ef4444, #10b981)',
                    animation: 'spin 2s linear infinite',
                    padding: '1px'
                  }}>
                    <div className="absolute inset-0 rounded-full bg-black"></div>
                  </div>
                </div>
              </div>
            )}
            <span className="hidden sm:inline relative z-10 transition-all duration-300 group-hover:font-semibold">
              {isAiAnalysisLoading ? (
                <span className="flex items-center gap-1">
                  <span className="animate-pulse">Analyzing</span>
                  <span className="animate-bounce delay-100">.</span>
                  <span className="animate-bounce delay-200">.</span>
                  <span className="animate-bounce delay-300">.</span>
                </span>
              ) : (
                'AI Analysis'
              )}
            </span>
            {/* Background gradient animation on hover */}
            <div className="absolute inset-0 rounded-md bg-gradient-to-r from-green-600/10 via-yellow-600/10 to-red-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </Button>
        </div>
      </div>

      {/* AI Analysis Error */}
      {aiAnalysisError && (
        <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{getAiErrorMessage()}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAiError}
              className="ml-auto h-6 w-6 p-0 text-destructive hover:text-destructive/80"
            >
              ×
            </Button>
          </div>
        </div>
      )}

      {/* Main Tabs Component */}
      <WorkspaceMainTabs
        keywordData={keywordData}
        searchFilter={searchFilter}
        isLoading={isLoading}
        workspaceName={workspaceName}
        workspaceId={workspaceId}
        userId={userId}
        onAiAnalysis={onAiAnalysis}
        isAiAnalysisLoading={isAiAnalysisLoading}
        allKeywordDataCount={allKeywordData.length}
      />
    </div>
  );
};

export default WorkspaceDetailContent;